/*
 * Copyright (c) 2025 Daerik.com
 * This script may not be copied, reproduced or altered in whole or in part.
 * We check the Internet regularly for illegal copies of our scripts.
 * Do not edit or copy this script for someone else, because you will be held responsible as well.
 * This copyright shall be enforced to the full extent permitted by law.
 * Licenses to use this script on a single website may be purchased from Daerik.com
 * @Author: Daerik
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { ChevronLeft, ChevronRight, Bell, AlertCircle, Gift, DollarSign, ShoppingBag, CreditCard, Ticket, Wallet, X } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { cn } from '@/lib/utils'
import { useNotificationContext } from '@/providers/NotificationProvider'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import CardBrandIcon from '@/components/CardBrandIcon'
import type { Notification, NotificationType, NotificationPaymentData, MembershipNotificationData, EntryPassNotificationData, OrderReadyNotificationData } from '@/constants/notifications'

// Group notifications by time period
const groupNotifications = (notifications: Notification[]) => {
	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)

	const groups: { label: string; items: Notification[] }[] = []
	const newItems: Notification[] = []
	const todayItems: Notification[] = []
	const yesterdayItems: Notification[] = []
	const olderItems: Notification[] = []

	// Filter out invalid notifications
	const validNotifications = notifications.filter(n => n && n.id && n.createdAt)

	validNotifications.forEach((notification) => {
		const notifDate = new Date(notification.createdAt)

		if (!notification.read) {
			newItems.push(notification)
		} else if (notifDate >= today) {
			todayItems.push(notification)
		} else if (notifDate >= yesterday) {
			yesterdayItems.push(notification)
		} else {
			olderItems.push(notification)
		}
	})

	if (newItems.length > 0) groups.push({ label: 'New', items: newItems })
	if (todayItems.length > 0) groups.push({ label: 'Today', items: todayItems })
	if (yesterdayItems.length > 0) groups.push({ label: 'Yesterday', items: yesterdayItems })
	if (olderItems.length > 0) groups.push({ label: 'Earlier', items: olderItems })

	return groups
}

const getNotificationIcon = (type: NotificationType) => {
	const iconClass = 'h-5 w-5'

	const iconConfig: Record<NotificationType, { bg: string; icon: JSX.Element }> = {
		order_ready: {
			bg: 'bg-green-500/20',
			icon: <ShoppingBag className={cn(iconClass, 'text-green-500')} />
		},
		membership: {
			bg: 'bg-purple-500/20',
			icon: <CreditCard className={cn(iconClass, 'text-purple-500')} />
		},
		entry_pass: {
			bg: 'bg-blue-500/20',
			icon: <Ticket className={cn(iconClass, 'text-blue-500')} />
		},
		transaction: {
			bg: 'bg-green-500/20',
			icon: <DollarSign className={cn(iconClass, 'text-green-500')} />
		},
		alert: {
			bg: 'bg-orange-500/20',
			icon: <AlertCircle className={cn(iconClass, 'text-orange-500')} />
		},
		gift: {
			bg: 'bg-amber-600/20',
			icon: <Gift className={cn(iconClass, 'text-amber-600')} />
		},
		system: {
			bg: 'bg-yellow-500/20',
			icon: <Bell className={cn(iconClass, 'text-yellow-500')} />
		}
	}

	const config = iconConfig[type] || iconConfig.system

	return (
		<div className={cn('h-10 w-10 rounded-full flex items-center justify-center', config.bg)}>
			{config.icon}
		</div>
	)
}

const formatTimestamp = (isoString: string) => {
	const date = new Date(isoString)
	const now = new Date()
	const diffMs = now.getTime() - date.getTime()
	const diffMins = Math.floor(diffMs / 60000)
	const diffHours = Math.floor(diffMs / 3600000)
	const diffDays = Math.floor(diffMs / 86400000)

	if (diffMins < 1) return 'Just now'
	if (diffMins < 60) return `${diffMins}m ago`
	if (diffHours < 24) return `${diffHours}h ago`
	if (diffDays < 7) return `${diffDays}d ago`

	return date.toLocaleDateString()
}

const formatFullDate = (isoString: string) => {
	const date = new Date(isoString)
	return date.toLocaleString(undefined, {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	})
}

// Format date with ordinal suffix (e.g., "Monday, January 1st, 2025")
const formatDateWithOrdinal = (isoString: string) => {
	const date = new Date(isoString)
	const day = date.getDate()

	const getOrdinalSuffix = (n: number) => {
		const s = ['th', 'st', 'nd', 'rd']
		const v = n % 100
		return s[(v - 20) % 10] || s[v] || s[0]
	}

	const weekday = date.toLocaleDateString(undefined, { weekday: 'long' })
	const month = date.toLocaleDateString(undefined, { month: 'long' })
	const year = date.getFullYear()

	return `${weekday}, ${month} ${day}${getOrdinalSuffix(day)}, ${year}`
}

// Check if a string looks like an ISO date
const isISODateString = (value: string) => {
	return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
}

// Check if a string looks like a time (HH:MM or H:MM)
const isTimeString = (value: string) => {
	return /^\d{1,2}:\d{2}$/.test(value)
}

// Format time string to 12-hour format (e.g., "00:00" -> "12:00am", "13:30" -> "1:30pm")
const formatTimeString = (timeStr: string) => {
	const [hours, minutes] = timeStr.split(':').map(Number)
	const period = hours >= 12 ? 'pm' : 'am'
	const hour12 = hours % 12 || 12
	return `${hour12}:${minutes.toString().padStart(2, '0')}${period}`
}

// Capitalize first letter of a string
const capitalizeFirst = (str: string): string => {
	if (!str) return str
	return str.charAt(0).toUpperCase() + str.slice(1)
}

// Format notification data values
const formatDataValue = (key: string, value: unknown): string => {
	if (typeof value === 'string') {
		if (isISODateString(value)) {
			return formatDateWithOrdinal(value)
		}
		if (isTimeString(value)) {
			return formatTimeString(value)
		}
		// Capitalize first letter for string values
		return capitalizeFirst(value)
	}
	if (typeof value === 'object') {
		return JSON.stringify(value)
	}
	return capitalizeFirst(String(value))
}

// Normalize snake_case keys to camelCase (recursively for nested objects/arrays)
const snakeToCamel = (str: string): string =>
	str.replace(/_([a-zA-Z])/g, (_, c: string) => c.toUpperCase())

const normalizeKeys = (obj: unknown): unknown => {
	if (Array.isArray(obj)) return obj.map(normalizeKeys)
	if (obj !== null && typeof obj === 'object') {
		return Object.fromEntries(
			Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
				snakeToCamel(key),
				normalizeKeys(value)
			])
		)
	}
	return obj
}

// Check if order is for Room Service delivery
const isRoomServiceOrder = (data: Record<string, unknown> | undefined): boolean => {
	if (!data) return false
	const location = data.pickupLocation || data.location
	return typeof location === 'string' && location.toLowerCase().includes('room service')
}

// Get display label for notification data keys (handles Room Service special cases)
const getDataKeyLabel = (key: string, isRoomService: boolean): string => {
	// Special handling for Room Service orders
	if (isRoomService) {
		if (key === 'pickupLocation' || key === 'pickup_location') {
			return 'Prepared At'
		}
	}
	// Default: convert camelCase/snake_case to title case
	return key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()
}

// Get display value for notification data (handles Room Service special cases)
const getDataDisplayValue = (key: string, value: unknown, isRoomService: boolean): string => {
	// For Room Service, show "Kitchen" instead of "Room Service" for location
	if (isRoomService && (key === 'pickupLocation' || key === 'pickup_location' || key === 'location')) {
		return 'Kitchen'
	}
	return formatDataValue(key, value)
}

// Format message for Room Service orders
const formatNotificationMessage = (message: string, data: Record<string, unknown> | undefined): string => {
	if (!data) return message

	const isRoomService = isRoomServiceOrder(data)
	if (isRoomService) {
		let formatted = message
		// Replace "Room Service" with "Kitchen" in messages like "prepared at Room Service"
		formatted = formatted.replace(/at Room Service/gi, 'at Kitchen')
		// Replace "Pick up at Kitchen" with delivery message
		const roomNumber = data.roomNumber || data.room_number
		if (roomNumber) {
			formatted = formatted
				.replace(/Pick up at Kitchen\.?/i, `It will be delivered to Room ${roomNumber}.`)
				.replace(/Pick up at .+?\.?$/i, `It will be delivered to Room ${roomNumber}.`)
		} else {
			formatted = formatted
				.replace(/Pick up at Kitchen\.?/i, 'It will be delivered to your room.')
				.replace(/Pick up at .+?\.?$/i, 'It will be delivered to your room.')
		}
		return formatted
	}
	return message
}

const GUEST_TYPE_LABELS: Record<string, string> = {
	couples: 'Couples',
	single_ladies: 'Single Ladies',
	single_males: 'Single Males'
}

const formatShortDateRange = (start: string, end: string) => {
	const s = new Date(start)
	const e = new Date(end)
	const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
	return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`
}

const formatUsd = (value: string | number) => `$${Number(value).toFixed(2)}`

const formatSech = (value: string | number) =>
	`${Number(value).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} SECH`

interface NotificationItemProps {
	notification: Notification
	onPress: () => void
}

const NotificationItem = ({ notification, onPress }: NotificationItemProps) => {
	return (
		<div
			className="flex items-center gap-3 p-4 bg-[#3C3C3C] rounded-xl hover:bg-[#4C4C4C] transition-colors cursor-pointer"
			onClick={onPress}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => e.key === 'Enter' && onPress()}
		>
			<div className="flex items-center gap-3 flex-1">
				{!notification.read && (
					<div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
				)}
				{getNotificationIcon(notification.type)}
				<div className="flex-1 min-w-0">
					<p className={cn(
						'text-sm leading-snug line-clamp-2',
						notification.read ? 'text-[#BABABB]' : 'text-[#ffffff] font-medium'
					)}>
						{notification.title}
					</p>
					<p className="text-[#BABABB] text-xs mt-0.5 line-clamp-1">
						{formatNotificationMessage(notification.message, notification.data)}
					</p>
					<p className="text-[#8A8A8B] text-xs mt-1">
						{formatTimestamp(notification.createdAt)}
					</p>
				</div>
			</div>
			<ChevronRight className="h-5 w-5 text-[#BABABB] shrink-0" />
		</div>
	)
}

const EmptyState = () => {
	return (
		<div className="flex flex-col items-center justify-center flex-1 px-6">
			<div className="relative mb-8">
				<div className="w-24 h-20 bg-gradient-to-b from-blue-400 to-blue-600 rounded-2xl relative">
					<div className="absolute -top-3 -right-2 w-3 h-8">
						<div className="w-3 h-3 bg-red-500 rounded-sm" />
						<div className="w-0.5 h-5 bg-[#3C3C3C] mx-auto" />
					</div>
					<div className="absolute -bottom-2 -left-2 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center border-4 border-[#18181B]">
						<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
						</svg>
					</div>
					<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-1.5 bg-blue-700 rounded-full" />
				</div>
				<div className="w-4 h-6 bg-gradient-to-b from-blue-500 to-blue-700 mx-auto rounded-b-lg" />
			</div>

			<h2 className="text-[#ffffff] text-xl font-semibold mb-2">
				No Notifications Yet
			</h2>
			<p className="text-[#BABABB] text-sm text-center max-w-xs">
				Your notifications will appear here once you receive them.
			</p>
		</div>
	)
}

const LoadingState = () => {
	return (
		<div className="flex-1 px-5 py-8 space-y-4">
			{[1, 2, 3].map((i) => (
				<div key={i} className="animate-pulse">
					<div className="h-20 bg-[#3C3C3C] rounded-xl" />
				</div>
			))}
		</div>
	)
}

// ─── Notification detail components ─────────────────────────

const NotifDetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => {
	if (value == null) return null
	return (
		<div className="flex justify-between items-start gap-3 py-1">
			<span className="text-[#8A8A8B] text-[12px] font-figtree shrink-0">{label}</span>
			<span className="text-[#ffffff] text-[12px] font-medium font-figtree text-right">{value}</span>
		</div>
	)
}

const PaymentMethodDisplay = ({ data }: { data: NotificationPaymentData }) => {
	if (data.paymentMethod === 'CARD') {
		return data.cardLast4 ? (
			<span className="inline-flex items-center gap-1.5">
				<CardBrandIcon brand={data.cardBrand} width={28} />
				<span className="text-[#ffffff] text-[12px] font-medium font-figtree">
					••••{data.cardLast4}
				</span>
			</span>
		) : (
			<span className="text-[#ffffff] text-[12px] font-medium font-figtree">Card</span>
		)
	}

	return (
		<span className="inline-flex items-center gap-1.5">
			<Wallet className="h-4 w-4 text-orange-400 shrink-0" />
			<span className="text-[#ffffff] text-[12px] font-medium font-figtree">
				{data.walletAddress
					? `${data.walletAddress.slice(0, 6)}...${data.walletAddress.slice(-4)}`
					: 'Token'}
			</span>
		</span>
	)
}

const MembershipNotifDetails = ({ data }: { data: MembershipNotificationData }) => (
	<div className="space-y-0">
		<NotifDetailRow label="Plan" value={data.templateName} />
		<NotifDetailRow label="Guest Type" value={GUEST_TYPE_LABELS[data.guestType] ?? data.guestType} />
		<NotifDetailRow
			label="Duration"
			value={`${data.durationMonths} month${data.durationMonths > 1 ? 's' : ''}`}
		/>
		<NotifDetailRow label="Period" value={formatShortDateRange(data.startDate, data.endDate)} />
		<NotifDetailRow label="Invoice" value={data.invoiceId} />
	</div>
)

const EntryPassNotifDetails = ({ data }: { data: EntryPassNotificationData }) => (
	<div className="space-y-0">
		<NotifDetailRow label="Pass" value={data.passName} />
		<NotifDetailRow label="Guest Type" value={GUEST_TYPE_LABELS[data.guestType] ?? data.guestType} />
		<NotifDetailRow
			label="Valid For"
			value={new Date(data.validFor).toLocaleDateString('en-US', {
				weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
			})}
		/>
		<NotifDetailRow
			label="Hours"
			value={`${formatTimeString(data.startTime)} – ${formatTimeString(data.endTime)}`}
		/>
		<NotifDetailRow label="Invoice" value={data.invoiceId} />
	</div>
)

const OrderReadyNotifDetails = ({ data }: { data: OrderReadyNotificationData }) => (
	<div className="space-y-0">
		<NotifDetailRow label="Order #" value={data.orderId} />
	</div>
)

const FinancialBreakdown = ({ data }: { data: NotificationPaymentData }) => {
	// Use currency field if available, fall back to deriving from paymentMethod
	const denomination = data.currency ?? (data.paymentMethod === 'TOKEN' ? 'SECH' : 'USD')
	const fmt = (value: string | number) =>
		denomination === 'SECH' ? formatSech(value) : formatUsd(value)

	const totalDisplay = data.totalSech != null
		? formatSech(data.totalSech)
		: data.totalUsd != null
			? formatUsd(data.totalUsd)
			: null

	return (
		<div className="space-y-2 text-sm">
			<div className="flex justify-between">
				<span className="text-[#BABABB]">Subtotal</span>
				<span className="text-[#ffffff]">{fmt(data.subtotal)}</span>
			</div>
			{data.discountAmount != null && (
				<div className="flex justify-between text-green-400">
					<span>Discount</span>
					<span>{`-${fmt(data.discountAmount)}`}</span>
				</div>
			)}
			<div className="border-t border-dashed border-[#4C4C4C] my-2" />
			{data.taxBreakdown && data.taxBreakdown.length > 0
				? <div className="flex justify-between">
					<span className="text-[#BABABB]">Taxes & Fees</span>
					<span className="text-[#ffffff]">{fmt(data.taxBreakdown.reduce((sum, t) => sum + t.amount, 0))}</span>
				</div>
				: <div className="flex justify-between">
					<span className="text-[#BABABB]">Taxes & Fees</span>
					<span className="text-[#ffffff]">{fmt(data.taxAmount)}</span>
				</div>
			}
			<div className="flex justify-between font-semibold">
				<span className="text-[#ffffff]">Total</span>
				<span className="text-[#ffffff]">{totalDisplay}</span>
			</div>
		</div>
	)
}

// ─── Notification detail modal ──────────────────────────────

interface NotificationDetailModalProps {
	notification: Notification | null
	open: boolean
	onClose: () => void
}

const NotificationDetailModal = ({ notification, open, onClose }: NotificationDetailModalProps) => {
	if (!notification) return null

	const data = notification.data
		? normalizeKeys(notification.data) as Record<string, unknown>
		: undefined
	const isRoomService = isRoomServiceOrder(data)
	const displayMessage = formatNotificationMessage(notification.message, data)
	const hasPayment = data?.paymentMethod === 'CARD' || data?.paymentMethod === 'TOKEN'

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className="bg-[#2C2C2C] border-[#3C3C3C] max-w-md w-[calc(100%-2rem)]">
				<DialogHeader className="flex flex-row items-start gap-3 text-left">
					{getNotificationIcon(notification.type)}
					<div className="flex-1">
						<DialogTitle className="text-[#ffffff] text-base font-semibold">
							{notification.title}
						</DialogTitle>
						<p className="text-[#8A8A8B] text-xs mt-1">
							{formatFullDate(notification.createdAt)}
						</p>
					</div>
				</DialogHeader>

				<div className="mt-2">
					<p className="text-[#BABABB] text-sm whitespace-pre-wrap">
						{displayMessage}
					</p>

					{hasPayment ? (
						<div className="mt-4 p-3 bg-[#1C1C1C] rounded-lg">
							{notification.type === 'membership' && (
								<MembershipNotifDetails data={data as unknown as MembershipNotificationData} />
							)}
							{notification.type === 'entry_pass' && (
								<EntryPassNotifDetails data={data as unknown as EntryPassNotificationData} />
							)}
							{notification.type === 'order_ready' && (
								<OrderReadyNotifDetails data={data as unknown as OrderReadyNotificationData} />
							)}

							<div className="border-t border-[#3C3C3C] mt-2 pt-2">
								<NotifDetailRow
									label="Payment"
									value={<PaymentMethodDisplay data={data as unknown as NotificationPaymentData} />}
								/>
							</div>

							<div className="border-t border-[#3C3C3C] mt-2 pt-2">
								<FinancialBreakdown data={data as unknown as NotificationPaymentData} />
							</div>
						</div>
					) : data && Object.keys(data).length > 0 ? (
						<div className="mt-4 p-3 bg-[#1C1C1C] rounded-lg space-y-2">
							{Object.entries(data).map(([key, value]) => (
								<div key={key} className="flex justify-between text-sm gap-4">
									<span className="text-[#8A8A8B] capitalize shrink-0">
										{getDataKeyLabel(key, isRoomService)}
									</span>
									<span className="text-[#ffffff] text-right">
										{getDataDisplayValue(key, value, isRoomService)}
									</span>
								</div>
							))}
						</div>
					) : null}
				</div>

				<button
					onClick={onClose}
					className="mt-4 w-full py-2.5 bg-[#3C3C3C] hover:bg-[#4C4C4C] text-[#ffffff] rounded-lg text-sm font-medium transition-colors"
				>
					Close
				</button>
			</DialogContent>
		</Dialog>
	)
}

export default function NotificationsPage() {
	const [, navigate] = useLocation()
	const { notifications, isLoading, error, markAsRead, markAllAsRead, refetch, unreadCount } = useNotificationContext()
	const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)

	// Fetch full notification list when page loads and when page becomes visible
	useEffect(() => {
		refetch()

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				refetch()
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
	}, [refetch])

	const groupedNotifications = groupNotifications(notifications)
	const hasNotifications = notifications.length > 0

	const handleNotificationPress = async (notification: Notification) => {
		if (!notification.read) {
			await markAsRead(notification.id)
		}

		setSelectedNotification(notification)
	}

	const handleCloseModal = () => {
		setSelectedNotification(null)
	}

	return (
		<ErrorBoundary>
			<div className="min-h-screen bg-[#18181B] flex flex-col">
				<div className="max-w-5xl mx-auto w-full px-5 pb-8">
					{/* Header with back button */}
					<div
						role="banner"
						className="h-12 flex items-center justify-between gap-2 text-white mb-4 pt-3"
					>
						<div className="flex items-center gap-2">
							<button
								type="button"
								aria-label="Go back"
								onClick={() => navigate('/')}
								className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
							>
								<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
							</button>

							<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
								Notifications
							</h1>
						</div>

						{unreadCount > 0 && (
							<button
								type="button"
								onClick={markAllAsRead}
								className="text-sm text-blue-400 hover:text-blue-300"
							>
								Mark all read
							</button>
						)}
					</div>
				</div>

				{/* Error State */}
				{error && (
					<div className="px-5 py-4">
						<div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
							<p className="text-red-400 text-sm">{error}</p>
							<button
								onClick={refetch}
								className="mt-2 text-sm text-blue-400 hover:text-blue-300"
							>
								Try again
							</button>
						</div>
					</div>
				)}

				{/* Content */}
				{isLoading ? (
					<LoadingState />
				) : hasNotifications ? (
					<div className="flex-1 overflow-y-auto max-w-5xl mx-auto w-full px-5 pb-8">
						{groupedNotifications.map((group) => (
							<div key={group.label} className="mb-6">
								<h3 className="text-[#ffffff] text-sm font-medium mb-3 px-1">
									{group.label}
								</h3>
								<div className="space-y-2">
									{group.items.map((notification) => (
										<NotificationItem
											key={notification.id}
											notification={notification}
											onPress={() => handleNotificationPress(notification)}
										/>
									))}
								</div>
							</div>
						))}
					</div>
				) : (
					<EmptyState />
				)}

				<NotificationDetailModal
					notification={selectedNotification}
					open={selectedNotification !== null}
					onClose={handleCloseModal}
				/>
			</div>
		</ErrorBoundary>
	)
}
